import React, { useCallback, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { productClickViewDetails } from 'actions/AnalyticsActions'
import LayoutContainer from 'components/Common/LayoutContainer'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import TrackedCardCarousel from 'components/Common/TrackedCardCarousel'
import { FLASH_OFFERS_TYPES } from 'constants/offerList'
import OfferListHeading from 'components/OfferList/OfferListHeading'
import { OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import getOfferListKey from 'lib/offerListKey'
import SearchTile from 'components/OfferList/OfferListTiles/SearchOfferTiles/SearchTile'
import Heading from 'components/Luxkit/Typography/Heading'
import LastMinuteYAMLOfferList from 'components/Pages/FrontPage/LastMinuteYAMLOfferList'
import OfferList, { OfferListV2Item } from 'components/OfferList/v2/OfferList'
import { buildSearchStringFromFilters } from 'lib/searchUtils'
import YouMayAlsoLikeTile from 'components/OfferList/OfferListTiles/YouMayAlsoLikeTile/index'
import { useOfferList } from 'hooks/Offers/useOfferList'
import { fetchOffersById } from 'actions/OfferActions'
import { useAppSelector } from 'hooks/reduxHooks'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

interface Props extends App.HubPageComponentOffersLoader {
  content?: App.ContentHubPage;
  urlFilters: App.OfferListFilters;
  availableFilters: App.OfferListAvailableFilters;
  offerTypes: Array<App.OfferType>;
}

function OffersLoader(props: Props) {
  const { urlFilters, filters, inCarousel, content, offersToShow, title, offerTypes } = props
  const dispatch = useAppDispatch()

  const onProductClickView = useCallback((data) => {
    dispatch(productClickViewDetails(data))
  }, [dispatch])

  const combinedFilters = useMemo(() => ({
    ...urlFilters,
    ...filters,
    limit: offersToShow,
    frontPageVisibleOnly: true,
  }), [urlFilters, filters, offersToShow])

  const offerListExtras: Array<OfferListV2Item> = content ? [
    {
      id: 'heading',
      position: 'start',
      element: <OfferListHeading
        title={`We are currently sold out of our ${content.title} escapes!`}
        description={`Our travel experts are working hard on curating new ${content.title} offers. In the meantime check out our available offers in other exciting destinations.`}
      />,
      show: 'hasNoOffers',
    },
  ] : []

  let listOfferTypes = offerTypes ?? FLASH_OFFERS_TYPES
  if (listOfferTypes.includes(OFFER_TYPE_LAST_MINUTE) && listOfferTypes.length !== 1) {
    offerListExtras.push({
      position: 'end',
      id: 'LME-YMAL',
      element: <LastMinuteYAMLOfferList
        offerListFilters={combinedFilters}
      />,
    })
    listOfferTypes = listOfferTypes.filter(type => type !== OFFER_TYPE_LAST_MINUTE)
  }

  const [offerList] = useOfferList({ ...combinedFilters, offerTypes: listOfferTypes }, { disabled: !inCarousel })
  const offerState = useAppSelector(state => state.offer.offers)
  const offers = useMemo(() => {
    return offerList.offerIds.map(id => offerState[id]).filter(Boolean)
  }, [offerState, offerList])

  useEffect(() => {
    if (inCarousel) {
      // carousels don't have lazy loading/pagination of offers like actual offer lists
      // we need to fetch them manually
      dispatch(fetchOffersById(offerList.offerIds))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerList, inCarousel])

  return <LayoutContainer>
    <VerticalSpacer gap={32}>
      <Heading variant="heading3" as="h2" align="center">{title}</Heading>
      {!inCarousel && <OfferList
        interstitials={offerListExtras}
        maxOffers={offersToShow}
        filters={{ ...combinedFilters, offerTypes: listOfferTypes }}
        tileStyle="search"
      />}
      {inCarousel && !offerList.fetching && <>
        {offerList.offerIds.length === 0 && <OfferListHeading
          title={`We are currently sold out of our ${content.title} escapes!`}
          description={`Our travel experts are working hard on curating new ${content.title} offers. In the meantime check out our available offers in other exciting destinations.`}
        />}
        {offerList.offerIds.length === 1 && offers[0] && <SearchTile
          productClick={onProductClickView}
          offer={offers[0]}
        />}
        {offerList.offerIds.length > 1 &&
          <div>
            <TrackedCardCarousel pageSize={4}>
              {offers.map((offer, i) => <YouMayAlsoLikeTile
                key={offer.id}
                onClick={onProductClickView}
                position={i + 1}
                linkQuery={buildSearchStringFromFilters(combinedFilters)}
                offer={offer}
              />)}
            </TrackedCardCarousel>
          </div>}
      </>}
    </VerticalSpacer>
  </LayoutContainer>
}

const mapStateToProps = (state: App.State, ownProps: Props) => {
  const urlFilters = getUrlOfferListFilters(state)
  const key = ownProps.offerTypes && getOfferListKey({
    ...urlFilters,
    ...ownProps.filters,
    offerTypes: ownProps.offerTypes.filter(type => type !== OFFER_TYPE_LAST_MINUTE),
  })

  return ({
    urlFilters,
    availableFilters: state.offer.offerListFilterOptions[key]?.filters,
  })
}

export default connect(mapStateToProps)(OffersLoader)
